import { type FC, useMemo } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { useItManagementFeatureAvailability } from 'modules/it_management/hooks/useItManagementFeatureAvailability'
import type { OrderRecord } from 'modules/it_management/pages/device_catalog/useOrdersDataCollectionSource'
import { useOrdersDataCollectionSource } from 'modules/it_management/pages/device_catalog/useOrdersDataCollectionSource'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return convertDate(iso, 'backend', 'ui')
}

function formatTotal(price: string): string {
  const num = Number.parseFloat(price)
  if (Number.isNaN(num)) return '—'
  return formatCurrency(toCents(num))
}

const OrdersPage: FC = () => {
  const {
    isItManagementEnabled,
    loading: featureLoading,
    error: featureError,
  } = useItManagementFeatureAvailability()

  const source = useOrdersDataCollectionSource()

  const tableColumns = useMemo(
    () => [
      {
        label: i18n.t('it_management.device_catalog.orders.column_order_id'),
        sorting: 'id',
        render: (order: OrderRecord) => ({
          type: 'text' as const,
          value: order.displayId,
        }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_date'),
        sorting: 'createdAt',
        render: (order: OrderRecord) => ({
          type: 'text' as const,
          value: formatDate(order.createdAt),
        }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_items'),
        sorting: 'itemsCount',
        render: (order: OrderRecord) => ({
          type: 'text' as const,
          value: String(order.itemsCount),
        }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_billing_entity'),
        render: () => ({ type: 'text' as const, value: '—' }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_type'),
        render: () => ({ type: 'text' as const, value: '—' }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_status'),
        sorting: 'status',
        render: (order: OrderRecord) => ({
          type: 'text' as const,
          value: order.status,
        }),
      },
      {
        label: i18n.t('it_management.device_catalog.orders.column_total'),
        sorting: 'price',
        align: 'right' as const,
        render: (order: OrderRecord) => ({
          type: 'text' as const,
          value: formatTotal(order.price),
        }),
      },
    ],
    []
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns: tableColumns,
        },
      },
    ],
    [tableColumns]
  )

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={isItManagementEnabled}
      error={featureError}
      loading={featureLoading}
    >
      <div className="flex flex-col gap-6 px-4 py-5 md:px-6">
        <header>
          <h1 className="text-2xl font-semibold text-f1-foreground">
            {i18n.t('it_management.device_catalog.orders.title')}
          </h1>
          <p className="mt-1 text-base text-f1-foreground-secondary">
            {i18n.t('it_management.device_catalog.orders.description')}
          </p>
        </header>

        <OneDataCollection
          source={source}
          visualizations={visualizations}
          emptyStates={{
            'no-data': {
              title: i18n.t('it_management.device_catalog.orders.empty_title'),
              description: i18n.t('it_management.device_catalog.orders.empty_description'),
            },
          }}
        />
      </div>
    </ResponsivePage.Body>
  )
}

export default OrdersPage
